'use client';

import { type ChangeEvent, type HTMLAttributes, type ReactNode, type Ref, useCallback, useId, useMemo } from 'react';
import { clsx } from 'clsx';
import { useCombobox, useMultipleSelection } from 'downshift';
import { matchSorter } from 'match-sorter';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { sortByKey } from '@interxfi/utilities/sort';
import ClearIcon from '../../../icons/react/icon-clear';
import DisclosureDownIcon from '../../../icons/react/icon-disclosure-down';
import DisclosureUpIcon from '../../../icons/react/icon-disclosure-up';
import InfoIcon from '../../../icons/react/icon-info';
import { useControlledValue } from '../../../util/controlled';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button/plain-icon/plain-icon-button';
import { RemovablePill } from '../../pill/removable-pill';
import { Tooltip } from '../../tooltip/tooltip';
import { Typography } from '../../typography';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import {
  InputDropdown,
  type InputDropdownOption,
  type InputDropdownPagination,
  inputDropdownOptionToString,
  useFloatingDropdown,
} from '../shared/dropdown/input-dropdown';
import { useFocusWithin } from '../shared/use-focus-within';
import inputStyles from '../input.module.scss';
import styles from './multi-combobox-input.module.scss';

function buildOptions<T extends string = string>({
  options,
  search,
  hasControlledFilters,
  optionsToShow,
}: {
  options: InputDropdownOption<T>[];
  search: string;
  hasControlledFilters?: boolean;
  optionsToShow?: number;
}) {
  let result = options;

  if (!hasControlledFilters && search) {
    result = matchSorter(result, search, {
      keys: ['label', 'aliases'],
    });
  }

  result = sortByKey(result, (o) => o.group || '');

  if (optionsToShow) {
    result = result.slice(0, optionsToShow);
  }

  return result;
}

export type MultiComboboxInputEvent<T extends string = string> = Omit<ChangeEvent<HTMLSelectElement>, 'target'> & {
  target: {
    value: T[];
    name?: string;
  };
};

export type MultiComboboxInputOnChange<T extends string = string> = (event: MultiComboboxInputEvent<T>) => void;

export interface MultiComboboxInputProps<T extends string = string> extends Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'prefix'> {
  allowClear?: boolean;
  disabled?: boolean;
  error?: string;
  hasControlledFilters?: boolean;
  hint?: ReactNode;
  info?: ReactNode;
  inputValue?: string;
  isLoading?: boolean;
  label: string;
  name: string;
  noMatchingOptionsMessage?: ReactNode;
  onChange?: MultiComboboxInputOnChange<T>;
  options: InputDropdownOption<T>[];
  optionsToShow?: number;
  onInputValueChange?: (value: string) => void;
  pagination?: InputDropdownPagination;
  placeholder?: string;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  touched?: boolean;
  value: T[];
}

export function MultiComboboxInput<T extends string = string>({
  allowClear = true,
  className,
  disabled,
  error,
  hasControlledFilters,
  hint,
  info,
  id: providedId,
  inputValue: controlledInputValue,
  isLoading,
  label,
  name,
  noMatchingOptionsMessage = 'No results found',
  onChange,
  onInputValueChange,
  options,
  optionsToShow,
  pagination,
  placeholder,
  ref,
  required,
  touched,
  value = STABLE_EMPTY_ARRAY_REF,
  ...rest
}: MultiComboboxInputProps<T>) {
  const [_, handleFocusWithin, handleBlurWithin] = useFocusWithin();
  const generatedId = useId();
  const id = providedId || generatedId;
  const labelId = `${id}-label`;
  const menuId = `${id}-menu`;
  const inputId = `${id}-input`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);
  const [search, setSearch] = useControlledValue<string>(controlledInputValue, onInputValueChange, '');
  const computedOptions = useMemo(
    () =>
      buildOptions({
        options,
        search,
        hasControlledFilters,
        optionsToShow,
      }),
    [options, search, hasControlledFilters, optionsToShow],
  );
  const optionsWithoutSelected = useMemo(() => computedOptions.filter((option) => !value.includes(option.value)), [computedOptions, value]);

  const { getSelectedItemProps, getDropdownProps, addSelectedItem, selectedItems, removeSelectedItem, setSelectedItems } = useMultipleSelection({
    initialSelectedItems: computedOptions.filter((option) => value.includes(option.value)),
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.FunctionAddSelectedItem:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          onChange?.({
            target: {
              name,
              value: newSelectedItems?.map((item) => item.value) ?? [],
            },
          } as unknown as MultiComboboxInputEvent<T>);
          break;
        default:
          break;
      }
    },
  });

  const { getItemProps, getMenuProps, getLabelProps, getInputProps, getToggleButtonProps, highlightedIndex, isOpen, inputValue } = useCombobox({
    id,
    menuId,
    labelId,
    inputId,
    inputValue: search,
    onInputValueChange: ({ inputValue: newInputValue }) => {
      setSearch(newInputValue);
    },
    items: optionsWithoutSelected,
    itemToString: inputDropdownOptionToString,
    selectedItem: null,
    defaultHighlightedIndex: 0,
    stateReducer(_, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: true, highlightedIndex: 0, inputValue: '' }),
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            addSelectedItem(newSelectedItem);
          }

          if (type === useCombobox.stateChangeTypes.InputBlur) {
            setSearch('');
          }

          break;
        case useCombobox.stateChangeTypes.InputChange:
          setSearch(newInputValue ?? '');
          break;
        default:
          break;
      }
    },
  });

  const handleClear = useCallback(() => {
    onChange?.({
      target: {
        name,
        value: [],
      },
    } as unknown as MultiComboboxInputEvent<T>);

    setSelectedItems([]);
    setSearch('');
  }, [name, onChange, setSearch, setSelectedItems]);

  const { refs, floatingStyles } = useFloatingDropdown(computedOptions.length, isOpen);

  const listboxProps = {
    ...getMenuProps({ ref: refs.setFloating }),
    style: floatingStyles,
    hidden: !isOpen,
  };

  const hasValue = Boolean(selectedItems.length || placeholder);
  const isFloating = Boolean(hasValue || isOpen);

  return (
    <VerticalStack
      className={clsx(
        inputStyles.input,
        styles.multiComboboxInput,
        isFloating && styles.isFloating,
        isOpen && styles.isOpen,
        disabled && inputStyles.isDisabled,
        disabled && styles.disabled,
        className,
      )}
      onBlurCapture={handleBlurWithin}
      onFocusCapture={handleFocusWithin}
    >
      <VerticalStack ref={refs.setReference}>
        <HorizontalStack className={styles.activator}>
          <Label
            {...getLabelProps({ label, className: clsx(styles.label, isFloating && styles.isFloating) })}
            hasError={showError}
            isFloating={isFloating}
            required={required}
          />

          <HorizontalStack
            align="space-between"
            blockAlign="center"
            className={clsx(styles.triggerContainer, styles.inputElement, !showError && !hint && styles.lastInputElement)}
            wrap="nowrap"
          >
            <HorizontalStack align="space-between" blockAlign="center" className={styles.triggerSelection} wrap="nowrap">
              <HorizontalStack blockAlign="center" className={styles.selectionRail} gap={2} wrap="wrap">
                {selectedItems.map((selectedItem, index) => (
                  <RemovablePill
                    className={selectedItem.prefix ? styles.pillWithPrefix : undefined}
                    key={selectedItem.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(selectedItem);
                    }}
                    size="medium"
                  >
                    <HorizontalStack
                      align="start"
                      blockAlign="center"
                      className={styles.item}
                      wrap="nowrap"
                      {...getSelectedItemProps({ selectedItem, index })}
                    >
                      {selectedItem.prefix ? <span className={styles.prefix}>{selectedItem.prefix}</span> : null}
                      <Typography className={styles.option}>{selectedItem.label}</Typography>
                    </HorizontalStack>
                  </RemovablePill>
                ))}

                <input
                  aria-describedby={hint ? hintId : undefined}
                  aria-errormessage={showError ? errorId : undefined}
                  aria-invalid={showError}
                  aria-required={required}
                  className={styles.trigger}
                  placeholder={placeholder}
                  {...getInputProps({
                    ...getDropdownProps({ preventKeyAction: isOpen, ref }),
                    disabled,
                    ...(rest as unknown as HTMLAttributes<HTMLInputElement>),
                  })}
                />
              </HorizontalStack>
            </HorizontalStack>

            <HorizontalStack blockAlign="center" className={styles.iconContainer} gap={4} wrap="nowrap">
              {allowClear && !disabled && selectedItems.length > 0 ? (
                <button aria-controls={id} aria-label="Clear selection" className={styles.clearButton} onClick={handleClear} type="button">
                  <ClearIcon aria-hidden width={16} />
                </button>
              ) : null}

              {info ? (
                <Tooltip
                  activator={
                    <PlainIconButton>
                      <InfoIcon aria-hidden height={16} />
                    </PlainIconButton>
                  }
                  maxWidth={200}
                  side="top"
                  size="small"
                >
                  {info}
                </Tooltip>
              ) : null}

              <button
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                className={styles.toggleButton}
                type="button"
                {...getToggleButtonProps({ disabled })}
              >
                {isOpen ? (
                  <DisclosureUpIcon aria-hidden className={styles.toggleIcon} />
                ) : (
                  <DisclosureDownIcon aria-hidden className={styles.toggleIcon} />
                )}
              </button>
            </HorizontalStack>
          </HorizontalStack>
        </HorizontalStack>

        {hint ? (
          <InputHint className={clsx(styles.inputElement, !showError && styles.lastInputElement)} id={hintId}>
            {hint}
          </InputHint>
        ) : null}

        {showError ? (
          <ErrorMessage className={clsx(styles.inputElement, styles.lastInputElement)} id={errorId}>
            {error}
          </ErrorMessage>
        ) : null}
      </VerticalStack>

      <InputDropdown
        getItemProps={getItemProps as any}
        highlightedIndex={highlightedIndex}
        inputId={id}
        isLoading={isLoading}
        noOptionsMessage={inputValue ? noMatchingOptionsMessage : 'No options'}
        options={optionsWithoutSelected}
        pagination={pagination}
        selectedItemValue={value}
        {...listboxProps}
      />
    </VerticalStack>
  );
}
